import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { formatPermission } from '../utils/permissions';
import { SwitchAccountModal } from '../components/Auth/SwitchAccountModal';

export function Profile() {
  const { user, currentAccount, accounts, permission } = useAuth();
  const [switchModalOpen, setSwitchModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string } | null>(null);

  if (!user) {
    return null;
  }

  const handleSwitchAccount = (accountId: string, accountName: string) => {
    setSelectedAccount({ id: accountId, name: accountName });
    setSwitchModalOpen(true);
  };

  const handleCloseModal = () => {
    setSwitchModalOpen(false);
    setSelectedAccount(null);
  };

  return (
    <Layout>
      <div className="container-custom py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Profile</h1>

          {/* User Information */}
          <div className="bg-white shadow rounded-lg mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">User Information</h2>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1 text-base text-gray-900">{user.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">User ID</label>
                <p className="mt-1 text-base text-gray-900 font-mono text-sm">{user.id}</p>
              </div>
              {user.is_superadmin && (
                <div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    Superadmin
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Current Account */}
          {currentAccount && (
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Current Account</h2>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Account ID</label>
                  <p className="mt-1 text-base text-gray-900">{currentAccount.account_id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">UUID</label>
                  <p className="mt-1 text-base text-gray-900 font-mono text-sm">{currentAccount.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Permission Level</label>
                  <p className="mt-1">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      permission === 'management' ? 'bg-green-100 text-green-800' :
                      permission === 'order_placement' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {permission ? formatPermission(permission) : 'Unknown'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* All Accounts */}
          {accounts.length > 0 && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  All Accounts ({accounts.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-200">
                {accounts.map((account) => {
                  const isCurrentAccount = currentAccount?.id === account.id;
                  return (
                    <div key={account.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-base font-medium text-gray-900">
                              {account.account_id}
                            </p>
                            {isCurrentAccount && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                                Current
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 font-mono">{account.id}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            account.permission === 'management' ? 'bg-green-100 text-green-800' :
                            account.permission === 'order_placement' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {formatPermission(account.permission as any)}
                          </span>
                          {!isCurrentAccount && (
                            <button
                              onClick={() => handleSwitchAccount(account.id, account.account_id)}
                              className="px-3 py-1 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                            >
                              Switch
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Switch Account Modal */}
      {selectedAccount && (
        <SwitchAccountModal
          isOpen={switchModalOpen}
          onClose={handleCloseModal}
          accountId={selectedAccount.id}
          accountName={selectedAccount.name}
        />
      )}
    </Layout>
  );
}
